import React, { useContext, useEffect, useMemo, useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min";
import { Icon } from "@iconify/react";
import { Link } from "react-router-dom";
import { ConfigContext } from "../context/GlobalContext";
import * as actionType from "../context/actions";
import DeleteInvoiceView from "./DeleteInvoiceView";
import PaginationView from "./PaginationView";
import { FilterdData } from "../helper/FilterdData";
import DataNotFoundView from "./DataNotFoundView";
import { useNavigate } from "react-router-dom";
import { Languages } from "../Utils/Languages";
import { PremiumFeatureOnlyModal } from "./PremiumFeatureOnlyModal";
import { Tooltip } from 'bootstrap/dist/js/bootstrap.bundle.min.js';
import { DownloadDocument } from "./preview/previewaction";
import DeleteMemberView from "./DeleteMemberView";

const AgencyMembersTableDataLayer = ({ selectedBusiness, membersData = [], onEditUser }) => {
    const navigate = useNavigate();
    const { state, dispatch } = useContext(ConfigContext);
    const [filterdInvoiceData, setFilterdInvoiceData] = useState(membersData);
    const [enrichedInvoices, setEnrichedInvoices] = useState([]);
    const [isDisplayPrimiumFeatureModel, setIsDisplayPrimiumFeatureModel] = useState(false)
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);

    // Enhanced function to enrich invoice data for better search
    const enrichInvoiceData = (invoices) => {
        return invoices.map((invoice, index) => {
            // Find customer name
            const matchedCustomer = state?.CUSTOMERS?.find(
                (cust) => cust.customerId?.toString() === invoice.customerId?.toString()
            );
            const customerName = matchedCustomer?.customer || "Unknown";

            // Format issued date
            const issuedDate = new Date(parseInt(invoice.date?.length > 10 ? parseInt(invoice.date) : parseInt(invoice.date) * 1000)).toDateString();

            // Format status
            const paidStatus = (invoice.paidFlagKey === "true" || invoice.paidFlagKey === true)
                ? Languages[state?.LANGUAGE]?.paid || "Paid"
                : Languages[state?.LANGUAGE]?.unpaid || "Unpaid";

            // Calculate due status
            let dueStatus = "No Due Date";
            if (invoice.dueDate) {
                let dueDateNum = parseInt(invoice.dueDate);
                if (dueDateNum.toString().length === 10) {
                    dueDateNum = dueDateNum * 1000;
                }
                const dueDate = new Date(dueDateNum);
                const today = new Date();
                dueDate.setHours(0, 0, 0, 0);
                today.setHours(0, 0, 0, 0);
                const diffTime = dueDate.getTime() - today.getTime();
                const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

                if (diffDays === 0) {
                    dueStatus = "Due Today";
                } else if (diffDays < 0) {
                    dueStatus = `Overdue ${Math.abs(diffDays)} ${Math.abs(diffDays) === 1 ? "day" : "days"}`;
                } else {
                    dueStatus = `Due in ${diffDays} ${diffDays === 1 ? "day" : "days"}`;
                }
            }

            return {
                ...invoice,
                srNo: index + 1,
                customerName, // Add searchable customer name
                issuedDateFormatted: issuedDate, // Add searchable formatted date
                paidStatusText: paidStatus, // Add searchable status text
                dueStatusText: dueStatus // Add searchable due status text
            };
        });
    };
    useEffect(() => {
        setFilterdInvoiceData(FilterdData(state, membersData, "date"));
    }, [state])
    const matchedInvoices = useMemo(() => {
        const filteredData = (selectedBusiness === null || selectedBusiness === "null")
            ? filterdInvoiceData
            : filterdInvoiceData.filter(invoice => invoice?.companyId == selectedBusiness);

        return enrichInvoiceData(filteredData);
    }, [filterdInvoiceData, selectedBusiness, state?.CUSTOMERS, state?.LANGUAGE]);
    const totalPages = Math.ceil(matchedInvoices?.length / itemsPerPage);

    const membersList = useMemo(() => {

        // 🔹 Find business unique id from companyId
        const selectedBusinessObj = state?.BUSINESSES?.find(
            (b) => String(b.companyId) === String(selectedBusiness)
        );

        const businessUniqueId = selectedBusinessObj?.id;

        const filteredMembers =
            !selectedBusiness || selectedBusiness === "null"
                ? membersData
                : membersData.filter((user) =>
                    user.company?.includes(businessUniqueId)
                );

        return filteredMembers.map((user, index) => ({
            ...user,
            srNo: index + 1,
            companyCount: user.company?.length || 0,
            createdAtFormatted: new Date(parseInt(user.createdAt)).toDateString()
        }));

    }, [membersData, selectedBusiness, state?.BUSINESSES]);
    useEffect(() => {
        // Select all elements with data-bs-toggle="DefaultTooltipThree"
        const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="DefaultTooltipThree"]');
        // Initialize tooltips
        const tooltipList = Array.from(tooltipTriggerList).map(
            (tooltipTriggerEl) => new Tooltip(tooltipTriggerEl)
        );
        // Cleanup on unmount
        return () => {
            tooltipList.forEach((tooltip) => tooltip.dispose());
        };
    }, []);

    useEffect(() => {
        if (state?.CUSTOMERS && Array.isArray(membersData)) {
            const enriched = membersData.map((invoice) => {
                const matchedCustomer = state.CUSTOMERS.find(
                    (cust) => cust.customerId === invoice.customerId
                );

                return {
                    ...invoice,
                    customerName: matchedCustomer?.customer || "Unknown",
                };
            });

            setEnrichedInvoices(enriched);
        }
    }, [state?.CUSTOMERS, membersData]);
    useEffect(() => {
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(totalPages);
        }
    }, [totalPages, currentPage]);

    const sortCaret = (order, column) => {
        return (
            <span className="d-inline-flex align-items-center ms-2" >
                {!order ? (
                    <Icon icon="mdi:swap-vertical" className="text-muted" />
                ) : order === "asc" ? (
                    <Icon icon="mdi:arrow-up-thick" className="text-muted" />
                ) : (
                    <Icon icon="mdi:arrow-down-thick" className="text-muted" />
                )}
            </span>
        );
    };


    const { SearchBar } = Search;
    const isPremium = state?.PREMIUM;

    const columns = [
        {
            dataField: "srNo",
            text: "S.R",
            align: "center"
        },
        {
            dataField: "name",
            text: "Member Name",
            sort: true
        },
        {
            dataField: "uid",
            text: "Member UID",
            style: { minWidth: "220px" }
        },
        {
            dataField: "company",
            text: "Assigned Businesses",
            align: "left",
            headerAlign: "left",
            formatter: (companies) => {

                if (!companies || companies.length === 0) {
                    return <span className="text-muted">None</span>;
                }

                return (
                    <div className="d-flex flex-column gap-1 align-items-start">
                        {companies.map((companyId, index) => {

                            const business = state?.BUSINESSES?.find(
                                (b) => String(b.id) === String(companyId)
                            );

                            return (
                                <span
                                    key={index}
                                    className={`d-inline-block text-start px-12 py-4 rounded-pill fw-medium text-sm bg-primary-50 text-primary-600 border border-primary-100`}
                                >
                                    {business ? (business.isShared ? `${business.companyName} (Shared by: ${business.ownerName})` : business.companyName) : companyId}
                                </span>
                            );
                        })}
                    </div>
                );
            }
        },

        {
            dataField: "createdAtFormatted",
            text: "Assigned On",
            formatter: (cell) => cell,
            sort: true,
            style: { minWidth: "120px" },
            sortCaret: sortCaret,
            sortFunc: (a, b, order) => {

                const dateA = new Date(a);
                const dateB = new Date(b);

                if (order === "asc") {
                    return dateA - dateB;
                }

                return dateB - dateA;
            }
        },
        {
            dataField: "platform",
            text: "Platform",
            align: "center"
        },
        {
            dataField: "providerId",
            text: "Provider",
            align: "center",
            formatter: (cell) => {
                const provider = cell || "password";
                let icon = "mdi:email-outline";
                let color = "text-secondary-light";

                if (provider.includes("google")) {
                    icon = "logos:google-icon";
                    color = "";
                } else if (provider.includes("facebook")) {
                    icon = "logos:facebook";
                    color = "";
                } else if (provider.includes("apple")) {
                    icon = "logos:apple";
                    color = "";
                } else if (provider === "phone") {
                    icon = "mdi:phone";
                    color = "text-primary-600";
                }

                return (
                    <div className="d-flex align-items-center justify-content-center">
                        <span className="badge bg-primary-50 text-primary-600 px-12 py-6 rounded-pill d-flex align-items-center gap-2 border border-primary-200">
                            <Icon icon={icon} className={color} width={16} />
                            <span className="text-xxs text-uppercase fw-bold">{provider.split(".")[0]}</span>
                        </span>
                    </div>
                );
            }
        },
        {
            dataField: "version",
            text: "Version",
            align: "center"
        },
        {
            dataField: "action",
            text: Languages[state?.LANGUAGE]?.action,
            headerAlign: "center",
            align: "center",
            searchable: false, // Exclude action column from search
            formatter: (cell, row) => (
                <>
                    <Link
                        onClick={() => onEditUser(row)}
                        className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                        data-bs-toggle="DefaultTooltipThree"
                        data-bs-placement="top"
                        data-bs-custom-class="tooltip-success"
                        data-bs-title="Edit Members"
                    >
                        <Icon icon="lucide:edit" />
                    </Link>
                    <Link
                        data-bs-toggle="DefaultTooltipThree"
                        data-bs-placement="top"
                        data-bs-custom-class="tooltip-danger"
                        data-bs-title="Delete Members"
                    >
                        {(() => {
                            const selectedBusinessObj = state?.BUSINESSES?.find(b => b.companyId === selectedBusiness);
                            const targetUid = selectedBusinessObj?.isShared ? selectedBusinessObj.ownerUid : state?.USERID;
                            return <DeleteMemberView FirebaseId={row?.uid} targetUid={targetUid} />;
                        })()}
                    </Link>
                </>
            ),
        }
    ];

    const OnClickOfRow = (invoiceId, isPaid) => {
        dispatch({ type: actionType.SELECTEDINVOICE, payload: invoiceId })
        if (isPaid == "true" || isPaid == true) {
            navigate("/invoice-preview")
        }
        else {
            navigate("/invoice-edit")
        }
    }

    const handleSelection = (val) => {
        setItemsPerPage(val)
        setCurrentPage(1)
    };

    return (
        <div className="card">
            <div className="card-body">
                <ToolkitProvider
                    keyField="uid"
                    data={membersList} // all invoices
                    columns={columns}
                    search
                >
                    {props => {
                        // Get current search text
                        const searchText = props.searchProps.searchText?.toLowerCase() || "";

                        // Filter full matchedInvoices based on search
                        const filteredData = membersList.filter(user => {

                            return (
                                user.name?.toLowerCase().includes(searchText) ||
                                user.uid?.toLowerCase().includes(searchText) ||
                                user.platform?.toLowerCase().includes(searchText)
                            );

                        });

                        // Apply pagination AFTER search
                        const start = (currentPage - 1) * itemsPerPage;
                        const paginatedSearchData = filteredData.slice(start, start + itemsPerPage);

                        return (
                            <>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <SearchBar
                                        {...props.searchProps}
                                        placeholder={"Search Members"}
                                        className="form-control w-auto"
                                    />
                                </div>
                                <div className="table-responsive">
                                    <BootstrapTable
                                        {...props.baseProps}
                                        bootstrap4
                                        bordered={false}
                                        data={paginatedSearchData} // use paginated filtered data
                                        wrapperClasses={`custom-table-border invoice-table ${localStorage.getItem("theme") == "dark" ? "table-dark" : "table-light"}`}
                                        defaultSorted={{
                                            dataField: "date",
                                            order: "desc",
                                        }}
                                        noDataIndication={() => <DataNotFoundView height={400} />}
                                        rowStyle={{ cursor: "pointer" }}
                                    />
                                </div>

                                {filteredData.length > 0 && (
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div className="d-flex align-items-center gap-2 entry_per_page">
                                            <div className="dropdown">
                                                <button
                                                    className="btn btn-outline-primary-600 not-active px-18 py-6 dropdown-toggle toggle-icon"
                                                    type="button"
                                                    data-bs-toggle="dropdown"
                                                    aria-expanded="false"
                                                >
                                                    {itemsPerPage}
                                                </button>
                                                <ul className="dropdown-menu shadow-md">
                                                    {[5, 10, 15, 20].map((value) => (
                                                        <li key={value}>
                                                            <Link
                                                                className={`dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 ${itemsPerPage == value && "bg-primary-50"
                                                                    }`}
                                                                onClick={() => handleSelection(value)}
                                                            >
                                                                {value}
                                                            </Link>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                            <span className="text-secondary-light">
                                                {Languages[state?.LANGUAGE]?.entries_per_page}
                                            </span>
                                        </div>

                                        <div className="ms-auto">
                                            <PaginationView
                                                currentPage={currentPage}
                                                totalPages={Math.ceil(filteredData.length / itemsPerPage)} // 👈 based on search results
                                                handlePageChange={setCurrentPage}
                                            />
                                        </div>
                                        {/* <PaginationView
                                            currentPage={currentPage}
                                            totalPages={Math.ceil(filteredData.length / itemsPerPage)}
                                            handlePageChange={setCurrentPage}
                                        /> */}
                                    </div>
                                )}
                            </>
                        );
                    }}
                </ToolkitProvider>



                <PremiumFeatureOnlyModal
                    isOpen={isDisplayPrimiumFeatureModel}
                    onClose={() => setIsDisplayPrimiumFeatureModel(false)}
                    featureName="Sales-Return"
                />
            </div>
        </div>
    );
};

export default AgencyMembersTableDataLayer;